import { Query, Relation } from "@nozbe/watermelondb";
import {
  children,
  date,
  field,
  json,
  lazy,
  relation,
  text,
  writer,
} from "@nozbe/watermelondb/decorators";
import { Associations } from "@nozbe/watermelondb/Model";
import {
  distinctUntilChanged,
  map,
  Observable,
  switchMap,
} from "@nozbe/watermelondb/utils/rx";
import {
  Payment,
  SaleAdjustmentType,
} from "@towersystems/roam-common/lib/generated-types";
import { combineLatest, from } from "rxjs";
import { v4 as uuid } from "uuid";
import { SaleStateMachine } from "../../../modules/point-of-sale/utils/sale";
import {
  compareMoreThanEqualsWithTolerance,
  compareWithTolerance,
} from "../../compare-with-tolerance";
import { round2Decimal } from "../../round-cash";
import { SyncState } from "../constants";
import { noopSanitizeJSON } from "../utils";
import Customer from "./Customer";
import Location from "./Location";
import PaymentMethod from "./PaymentMethod";
import Product from "./Product";
import SaleItem from "./SaleItem";
import Staff from "./Staff";
import { SyncMeta } from "./SyncMeta";
import Terminal from "./Terminal";

export type SalePayment = { localId: string; id?: string } & Omit<
  Payment,
  "id" | "__typename"
>;

export type PaymentState = "Settled" | "Error";

export const receiptHtmlMetadataKey = "receiptHtml";

export interface AddItemInput {
  productId: string;
  quantity: number;
  basePrice: number;
  discount: number;
  stockNumber?: string;
  taxRatePercent: number;
  stockCode?: string;
  serialNumber?: string;
  name?: string;
  giftCardNumber?: string;
  metadata?: { [key: string]: any };
}

export interface AddPaymentInput {
  paymentMethodId: string;
  amount: number;
  paymentDate: Date;
  transactionId?: string;
  paymentState?: PaymentState;
  name?: string;
  metadata?: { [key: string]: any };
}

export enum SaleState {
  OPEN = "OPEN",
  CANCELLED = "CANCELLED",
  CLOSED = "CLOSED",
  SAVED = "SAVED",
  VOIDED = "VOIDED",
  LAYBY_OPEN = "LAYBY_OPEN",
  LAYBY_SAVED = "LAYBY_SAVED",
  LAYBY_CLOSED = "LAYBY_CLOSED",
  INVOICE_OPEN = "INVOICE_OPEN",
  INVOICE_SAVED = "INVOICE_SAVED",
  INVOICE_CLOSED = "INVOICE_CLOSED",
  QUOTE_CLOSED = "QUOTE_CLOSED",
  QUOTE_OPEN = "QUOTE_OPEN",
  QUOTE_SAVED = "QUOTE_SAVED",
  LAYBY_CANCELLED = "LAYBY_CANCELLED",
  INVOICE_CANCELLED = "INVOICE_CANCELLED",
  QUOTE_CANCELLED = "QUOTE_CANCELLED",
  PROFORMA_CLOSED = "PROFORMA_CLOSED",
  PROFORMA_SAVED = "PROFORMA_SAVED",
}

export default class Sale extends SyncMeta {
  static table = "sales";

  static associations: Associations = {
    sale_items: { type: "has_many", foreignKey: "sale_id" },
  };

  @text("receipt_to") receiptTo: string | null;
  @text("invoice_number") invoiceNumber: string | null;
  @field("invoice_sequence") invoiceSequence: number | null;
  @text("external_id") externalId: string | null;
  @text("sale_state") saleState: SaleState;
  @text("sync_state") syncState: SyncState;
  @field("num_sync_attempts") numSyncAttempts: number;
  @date("last_sync_attempt") lastSyncAttempt: Date | null;
  @field("is_refund") isRefund: boolean;
  @field("quote_accepted") quoteAccepted: boolean | null;
  @field("deposit_percent") depositPercent: number | null;
  @json("payments", noopSanitizeJSON) payments: SalePayment[];
  @json("metadata", noopSanitizeJSON) metadata: { [key: string]: any } | null;
  @text("note") note: string | null;
  @text("purchase_order_number") purchaseOrderNumber: string | null;
  @text("gift_voucher_code") giftVoucherCode: string | null;

  @relation("locations", "location_id") location: Relation<Location>;
  @relation("terminals", "terminal_id") terminal: Relation<Terminal>;
  @relation("staffs", "staff_id") staff: Relation<Staff>;

  @relation("customers", "customer_id") customer: Relation<Customer>;
  @date("sale_completed_at") saleCompletedAt: Date | null;
  @date("expected_pickup_date") expectedPickupDate: Date | null;

  @children("sale_items")
  items: Query<SaleItem>;

  @writer async addItem(item: AddItemInput) {
    await this.callReader(() => this.assertCanModifySale());

    const product = await this.collections
      .get<Product>("products")
      .find(item.productId);

    if (product.isGiftCard) {
      if (!item.giftCardNumber) {
        throw new Error("error.gift-card-number-required");
      }

      if (item.quantity !== 1) {
        throw new Error("error.gift-card-quantity");
      }

      if (item.basePrice <= 0) {
        throw new Error("error.gift-card-amount");
      }
    }

    let productKit = null;

    if (product.isKit) {
      productKit = await product.productKit.fetch();
      await productKit.items.fetch();
    }

    const customer = await this.customer.fetch();
    const isTradeCustomer = customer?.isTradeCustomer || false;

    // check

    const newItem = await this.collections
      .get<SaleItem>("sale_items")
      .create((i) => {
        i.sale.set(this);
        i.product.set(product);
        i.quantity = item.quantity;
        i.basePrice = item.basePrice;
        i.taxRatePercent = item.taxRatePercent;
        i.adjustments = [];
        i.stockCode = item.stockCode || null;
        i.stockNumber = item.stockNumber || null;
        i.metadata = item.metadata || {};
        i.serialNumber = item.serialNumber || null;
        i.loyaltyPointEarnRate = product.loyaltyPointEarnValue || 0;
        i.giftCardNumber = item.giftCardNumber || null;
        i.name = item.name ?? product.name;
        if (!!productKit) {
          i.productKit.set(productKit);
        }
      });

    if (isTradeCustomer) {
      this.callWriter(() => newItem.applyTradePrice());
    }

    return newItem;
  }

  async calculatePotentialTradePriceSavings() {
    const items = await this.items.fetch();
    let savings = 0;
    for (const item of items) {
      savings += await item.calculateTradePriceSavings();
    }
    return savings;
  }

  async applyGiftVoucher(code: string, amount: number) {
    await this.setGiftVoucherCode(code);
    await this.discountSale(
      amount,
      SaleAdjustmentType.GIFT_VOUCHER_CODE,
      false
    );
  }

  async discountSale(
    discount: number,
    type = SaleAdjustmentType.SALE_DISCOUNT,
    errorOnGreaterDiscount = true
  ) {
    if (
      false ==
      [
        SaleAdjustmentType.GIFT_VOUCHER_CODE,
        SaleAdjustmentType.SALE_DISCOUNT,
      ].includes(type)
    ) {
      throw new Error("error.illegal-discount-type");
    }

    const totalPrice = await this.getMaxDiscountAmount_ASYNC();

    if (compareMoreThanEqualsWithTolerance(discount, totalPrice)) {
      if (errorOnGreaterDiscount) {
        throw new Error("error.discount-greater-than-total");
      } else {
        discount = totalPrice;
      }
    }

    const discountableItems = (await this.getDiscountableItems()).sort(
      (a, b) => a.price - b.price
    );

    let numOfDiscountableItemsRemaining = discountableItems.length;
    let remainingDiscountAmount = discount;

    for (const item of discountableItems) {
      const maxDiscount = item.totalPrice;

      const discount = Math.min(
        maxDiscount,
        remainingDiscountAmount / numOfDiscountableItemsRemaining
      );

      const discountPerItem = discount / item.quantity;

      await item.addAdjustment({
        type: type,
        amount: discountPerItem,
      });

      remainingDiscountAmount -= discount;
      numOfDiscountableItemsRemaining--;
    }
  }

  async removeSaleDiscount() {
    for (const item of await this.items.fetch()) {
      await item.removeAdjustment(SaleAdjustmentType.SALE_DISCOUNT);
    }
  }

  async removeSaleTradePriceAdjustments() {
    for (const item of await this.items.fetch()) {
      await item.removeAdjustment(SaleAdjustmentType.TRADE_PRICE_ADJUSTMENT);
    }
  }

  async removeGiftVoucherRedemptions() {
    for (const item of await this.items.fetch()) {
      await item.removeAdjustment(SaleAdjustmentType.GIFT_VOUCHER_CODE);
    }
    await this.setGiftVoucherCode("");
  }

  @writer async setGiftVoucherCode(code: string) {
    await this.update((item) => {
      item.giftVoucherCode = code;
    });
  }

  @writer async setExpectedPickupDate(date: Date | null) {
    await this.update((item) => {
      item.expectedPickupDate = date;
    });
  }

  async removeLoyaltyDiscount() {
    for (const item of await this.items.fetch()) {
      await item.removeAdjustment(SaleAdjustmentType.LOYALTY_DISCOUNT);
    }
  }

  @writer async setQuoteAcccepted(quoteAccepted: boolean) {
    await this.update((item) => {
      item.quoteAccepted = quoteAccepted;
    });
  }

  @writer async addMetadata(key: string, value: any) {
    await this.update((item) => {
      item.metadata = { ...item.metadata, [key]: value };
    });
  }

  /**
   * Cancel the sale with the correct state for Invoice, Layby, Quote and Sale
   */
  async cancelSale(dryRun: boolean = false) {
    switch (this.saleState) {
      case SaleState.OPEN:
      case SaleState.SAVED:
        await this.transitionToState(SaleState.CANCELLED, dryRun);
        break;
      case SaleState.LAYBY_OPEN:
      case SaleState.LAYBY_SAVED:
        await this.transitionToState(SaleState.LAYBY_CANCELLED, dryRun);
        break;
      case SaleState.INVOICE_OPEN:
      case SaleState.INVOICE_SAVED:
        await this.transitionToState(SaleState.INVOICE_CANCELLED, dryRun);
        break;
      case SaleState.QUOTE_OPEN:
      case SaleState.QUOTE_SAVED:
        await this.transitionToState(SaleState.QUOTE_CANCELLED, dryRun);
        break;
      default:
        throw new Error("error.illegal-state");
    }
  }

  @writer async addPayment(input: AddPaymentInput) {
    const paymentMethod = await this.collections
      .get<PaymentMethod>("payment_methods")
      .find(input.paymentMethodId);

    const payType = await paymentMethod.payType.fetch();

    return await this.update((item) => {
      const payment: SalePayment = {
        localId: uuid(),
        paymentMethodId: input.paymentMethodId,
        amount: input.amount,
        paymentDate: input.paymentDate,
        paymentTypeId: paymentMethod.payType.id,
        label: payType.name,
        name: input.name ?? paymentMethod.name,
        metadata: input.metadata,
        transactionId: input.transactionId,
        state: input.paymentState || "Settled",
      };
      item.payments = [...(item.payments || []), payment];
      return payment;
    });
  }

  @writer async addCustomer(customerId: string) {
    if (![SaleState.OPEN, SaleState.SAVED].includes(this.saleState)) {
      throw new Error("Cannot add customer to a non-open sale");
    }
    await this.collections.get<Customer>("customers").find(customerId);

    await this.update((item) => {
      item.customer.id = customerId;
    });

    // if trade customer, apply trade price
    const customer = await this.customer.fetch();
    if (customer.isTradeCustomer) {
      this.callWriter(() => this.applyTradePriceToSaleItems());
    }
  }

  @writer async applyTradePriceToSaleItems() {
    const items = await this.items.fetch();
    for (const item of items) {
      await item.applyTradePrice();
    }
  }

  @writer async updateSaleNote(note: string = "") {
    await this.update((item) => {
      item.note = note;
    });
  }

  async removeCustomer() {
    await this.internalRemoveCustomer();
    const items = await this.items.fetch();
    for (const item of items) {
      await item.removeAdjustment(SaleAdjustmentType.LOYALTY_DISCOUNT);
      await item.removeAdjustment(SaleAdjustmentType.TRADE_PRICE_ADJUSTMENT);
    }
  }

  @writer private async internalRemoveCustomer() {
    if (
      [
        SaleState.LAYBY_CLOSED,
        SaleState.LAYBY_OPEN,
        SaleState.LAYBY_SAVED,
        SaleState.INVOICE_CLOSED,
        SaleState.INVOICE_OPEN,
        SaleState.INVOICE_SAVED,
        SaleState.QUOTE_CLOSED,
        SaleState.QUOTE_OPEN,
        SaleState.QUOTE_SAVED,
      ].includes(this.saleState)
    ) {
      throw new Error(
        "error.can-not-remove-customer-from-layby-invoice-or-quote"
      );
    }

    await this.update((item) => {
      item.customer.id = null;
    });
  }

  @writer async transitionToState(state: SaleState, dryRun = false) {
    const fsm = new SaleStateMachine();
    await fsm.transition(this, state, dryRun);
  }

  @writer async updateSaleState(state: SaleState) {
    await this.update((item) => {
      item.saleState = state;
    });
  }

  @writer async updateDepositPercent(depositPercent: number | null) {
    await this.update((item) => {
      item.depositPercent = depositPercent;
    });
  }

  @writer async updateReceiptTo(emailAddress: string | null) {
    if (emailAddress) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!emailRegex.test(emailAddress)) {
        throw new Error("Invalid email address");
      }
    }

    await this.update((item) => {
      item.receiptTo = emailAddress;
    });
  }

  @writer async removeItem(itemId: string) {
    await this.callReader(() => this.assertCanModifySale());
    throw new Error("Not implemented");
  }

  @writer async clearItems() {
    await this.callReader(() => this.assertCanModifySale());
    const items = await this.items.fetch();
    for (const item of items) {
      await item.destroyPermanently();
    }
  }

  async convertLayby(depositPercent: number) {
    if (!this.customer.id) {
      throw new Error("error.customer-required");
    }
    await this.updateDepositPercent(depositPercent);
    await this.transitionToState(SaleState.LAYBY_OPEN);
  }

  async revertLayby() {
    await this.updateDepositPercent(null);
    await this.transitionToState(SaleState.OPEN);
  }

  async convertInvoice() {
    if (!this.customer.id) {
      throw new Error("error.customer-required");
    }
    await this.transitionToState(SaleState.INVOICE_OPEN);
  }

  async revertInvoice() {
    await this.transitionToState(SaleState.OPEN);
  }

  getSaleItem = async (itemId: string) => {
    const items = await this.items.fetch();
    const item = items.find((i) => i.id === itemId);
    if (!item) {
      throw new Error("Item not found");
    }
    return item;
  };

  get paymentsTotal() {
    if (!this.payments) return 0;
    return this.payments
      .filter((p) => !p.state || p.state === "Settled")
      .reduce((acc, payment) => acc + payment.amount, 0);
  }

  get paymentsTotalObservable(): Observable<number> {
    return this.observe().pipe(
      map((sale) => sale.paymentsTotal),
      distinctUntilChanged()
    );
  }

  get numberOfPaymentsObservable(): Observable<number> {
    return this.observe().pipe(
      map((sale) => sale.payments?.length || 0),
      distinctUntilChanged()
    );
  }

  get balance(): Observable<number> {
    return from(combineLatest(this.totals, this.paymentsTotalObservable)).pipe(
      map(([totals, paymentsTotal]) => {
        const num = totals.totalPrice - paymentsTotal;
        return round2Decimal(num);
      })
    );
  }

  getMaxDiscountAmount_ASYNC = async () => {
    const items = await this.getDiscountableItems();
    return items.reduce((acc, i) => acc + i.totalPrice, 0);
  };

  getDiscountableItems = async () => {
    let discountableItems = [];
    const items = await this.items.fetch();
    for (const item of items) {
      const product = await item.product.fetch();
      if (product.discountable && !compareWithTolerance(item.price, 0)) {
        discountableItems.push(item);
      }
    }
    return discountableItems;
  };

  get canCreateAsLayby(): boolean {
    return (
      this.saleState === SaleState.OPEN &&
      this.customer.id &&
      this.settledPayments.length === 0
    );
  }

  get canTransitionCancel(): Promise<boolean> {
    return this.transitionToState(SaleState.CANCELLED, true)
      .then(() => true)
      .catch(() => false);
  }

  balance_ASYNC = async () => {
    const totalPrice = (await this.items.fetch()).reduce(
      (acc, i) => acc + i.totalPrice,
      0
    );

    const paymentsTotal = this.paymentsTotal;
    const num = totalPrice - paymentsTotal;
    return round2Decimal(num);
  };

  /**
   * Obeserve if the balance is ZERO.
   * Balance is the difference between the total price of the items
   * and the total payments.
   */
  @lazy balancePaidOff(): Observable<boolean> {
    return from(
      combineLatest(this.balance, this.numberOfPaymentsObservable)
    ).pipe(
      map(([balance, numberOfPaymentsObservable]) => {
        return (
          compareWithTolerance(balance, 0) && numberOfPaymentsObservable > 0
        );
      })
    );
  }

  /**
   * Return a promise to check if the balance is ZERO.
   * Balance is the difference between the total price of the items
   * and the total payments.
   */
  balancePaidOff_ASYNC = async () => {
    const numberOfPayments = this.settledPayments.length;
    const paymentsTotal = this.paymentsTotal;

    const totalPrice = (await this.items.fetch()).reduce(
      (acc: number, item: any) => acc + item.totalPrice,
      0
    );

    const balance = round2Decimal(totalPrice - paymentsTotal);

    return compareWithTolerance(balance, 0) && numberOfPayments > 0;
  };

  /**
   * This needs to be refactored to use the state machine
   */
  @writer async canTransitionToClosed_ASYNC(): Promise<boolean> {
    try {
      await this.callWriter(() =>
        this.transitionToState(this.closableState, true)
      );
      return true;
    } catch (e) {
      return false;
    }
  }

  async canModifySale() {
    try {
      await this.assertCanModifySale();
      return true;
    } catch (e) {
      return false;
    }
  }

  @writer async assertCanModifySale() {
    if (this.isProforma) {
      throw new Error("error.proforma-can-not-be-modified");
    }

    // all sale types besides sale can be modified until they are manually closed
    if (this.isClosed) {
      throw new Error("error.sale-closed");
    }

    // only sales that are open or saved can be modified
    if (this.isSale) {
      if (await this.callWriter(() => this.canTransitionToClosed_ASYNC())) {
        throw new Error("error.sale-ready-to-close");
      }
    }
  }

  @writer async setPurchaseOrderNumber(
    purchaseOrderNumber: string | undefined
  ) {
    await this.update((item) => {
      item.purchaseOrderNumber = purchaseOrderNumber ?? null;
    });
  }

  generatePurchaseOrderNumber() {
    // get the last 3 characters of the invoice number
    const orderNumber = this.invoiceSequence?.toString().slice(-3);
    return orderNumber;
  }

  @writer async completeQuote() {
    await this.batch(
      this.prepareUpdate((s) => {
        s.quoteAccepted = s.quoteAccepted || false;
        s.saleState = SaleState.QUOTE_CLOSED;
      })
    );
  }

  get saveableState() {
    let toState = SaleState.SAVED;
    switch (this.saleState) {
      case SaleState.PROFORMA_SAVED:
        toState = SaleState.PROFORMA_SAVED;
        break;
      case SaleState.OPEN:
        toState = SaleState.SAVED;
        break;
      case SaleState.LAYBY_SAVED:
      case SaleState.LAYBY_OPEN:
        toState = SaleState.LAYBY_SAVED;
        break;
      case SaleState.INVOICE_OPEN:
      case SaleState.INVOICE_SAVED:
        toState = SaleState.INVOICE_SAVED;
        break;
      case SaleState.QUOTE_OPEN:
      case SaleState.QUOTE_SAVED:
        toState = SaleState.QUOTE_SAVED;
        break;
      default:
        toState = SaleState.SAVED;
    }
    return toState;
  }

  get closableState() {
    let toState = SaleState.CLOSED;
    switch (this.saleState) {
      case SaleState.PROFORMA_SAVED:
      case SaleState.PROFORMA_CLOSED:
        toState = SaleState.PROFORMA_CLOSED;
        break;
      case SaleState.QUOTE_OPEN:
      case SaleState.QUOTE_SAVED:
      case SaleState.QUOTE_CLOSED:
        toState = SaleState.QUOTE_CLOSED;
        break;
      case SaleState.LAYBY_OPEN:
      case SaleState.LAYBY_SAVED:
      case SaleState.LAYBY_CLOSED:
        toState = SaleState.LAYBY_CLOSED;
        break;
      case SaleState.INVOICE_OPEN:
      case SaleState.INVOICE_SAVED:
      case SaleState.INVOICE_CLOSED:
        toState = SaleState.INVOICE_CLOSED;
        break;
      default:
        toState = SaleState.CLOSED;
    }
    return toState;
  }

  @writer async closeSale(dryRun = false) {
    let toState = this.closableState;

    if (this.saleState != toState) {
      await this.callWriter(() => {
        return this.transitionToState(toState, dryRun);
      });
    }

    if (dryRun) {
      return;
    }

    const customer = await this.customer.fetch();

    const totalLoyaltyPointsUsed = customer
      ? await this.getTotalLoyaltyPointsUsed()
      : 0;

    await this.batch(
      this.prepareUpdate((s) => {
        s.saleCompletedAt = new Date();
      }),
      customer && totalLoyaltyPointsUsed >= 0
        ? customer.prepareUpdate((c) => {
            c.loyaltyPoints = Math.max(
              c.loyaltyPoints - totalLoyaltyPointsUsed,
              0
            );
          })
        : undefined
    );
  }

  @writer async updateCompletedAtDate(date?: Date) {
    await this.update((item) => {
      item.saleCompletedAt = date || new Date();
    });
  }

  get canRemoveCustomer() {
    if (this.saleState.includes("CLOSED")) {
      return false;
    }

    if (true === this.isSale) {
      return true;
    }

    return false;
  }

  totals_ASYNC = async () => {
    const items = await this.items.fetch();

    const totalDiscount = items.reduce(
      (acc: number, item: any) => acc + item.totalDiscount,
      0
    );

    const totalPrice = items.reduce(
      (acc: number, item: any) => acc + item.totalPrice,
      0
    );

    const totalBasePrice = items.reduce(
      (acc: number, item: any) => acc + item.totalBasePrice,
      0
    );

    const totalDiscountPercent = Math.round(
      (totalDiscount / totalBasePrice) * 100
    );

    return {
      totalPrice: totalPrice,
      totalBasePrice: totalBasePrice,
      totalTax: items.reduce(
        (acc: number, item: any) => acc + item.totalTax,
        0
      ),
      totalDiscount: totalDiscount,
      totalDiscountPercent: totalDiscountPercent,
    };
  };

  getTotalLoyaltyPointsUsed = async () => {
    const items = await this.items.fetch();
    return items.reduce((acc, i) => {
      const p = i.adjustments.find(
        (a: any) => a.type === SaleAdjustmentType.LOYALTY_DISCOUNT
      );
      return acc + (p?.unitLoyaltyPointsUsed || 0) * i.quantity;
    }, 0);
  };

  //@ts-ignore
  @lazy totals = this.items
    .observeWithColumns(["quantity", "adjustments"])
    .pipe(
      map((items: SaleItem[]) => {
        return {
          totalPrice: items.reduce(
            (acc: number, item: any) => acc + item.totalPrice,
            0
          ),
          totalBasePrice: items.reduce(
            (acc: number, item: any) => acc + item.totalBasePrice,
            0
          ),
          totalTax: items.reduce(
            (acc: number, item: any) => acc + item.totalTax,
            0
          ),
          totalDiscount: items.reduce(
            (acc: number, item: any) => acc + item.totalDiscount,
            0
          ),
        };
      })
    );

  async applyCustomerLoyaltyPoints() {
    const customer = this.customer.id ? await this.customer.fetch() : null;
    if (!customer) {
      return;
    }

    const items = await this.items.fetch();
    const discountableItems = [];
    let pointsRemaining = customer.loyaltyPoints;

    for (const item of items) {
      if (await item.discountableItem_ASYNC()) {
        discountableItems.push(item);
      }
    }
    let i = discountableItems.length;
    while (pointsRemaining >= 1 && i >= 1) {
      const item = discountableItems[i - 1];
      const product = await item.product.fetch();
      const maxRedeemablePoints = await item.maxPossibleRedeemPoints_ASYNC();
      let discountAmount = 0;

      if (!product.loyaltyPointRedeemValue) {
        i--;
        continue;
      }

      let pointsUsed = 0;

      if (maxRedeemablePoints > pointsRemaining) {
        discountAmount = pointsRemaining / product.loyaltyPointRedeemValue;
        pointsUsed = pointsRemaining;
        pointsRemaining = 0;
      } else {
        discountAmount = maxRedeemablePoints / product.loyaltyPointRedeemValue;
        pointsUsed = pointsRemaining - maxRedeemablePoints;
        pointsRemaining = pointsUsed;
      }

      const discountPerItem = discountAmount / item.quantity;

      await item.addAdjustment({
        type: SaleAdjustmentType.LOYALTY_DISCOUNT,
        amount: discountPerItem,
        unitLoyaltyPointsUsed: Math.ceil(pointsUsed / item.quantity),
      });
      i--;
    }
  }

  @lazy maxDiscountGivenLoyaltyPoints = from(
    combineLatest(
      //@ts-ignore
      this.items.observeWithColumns(["quantity"]),
      //@ts-ignore
      this.customer
    )
  ).pipe(
    switchMap(async ([allItems, customer]) => {
      if (!customer) {
        return 0;
      }
      const discountableItems = [];
      let pointsRemaining = customer.loyaltyPoints;
      let pointsValue = 0;

      for (const item of allItems) {
        if (await item.discountableItem_ASYNC()) {
          discountableItems.push(item);
        }
      }
      let i = discountableItems.length;
      while (pointsRemaining >= 1 && i >= 1) {
        const item = discountableItems[i - 1];
        const product = await item.product.fetch();
        const maxRedeemablePoints = await item.maxPossibleRedeemPoints_ASYNC();
        if (maxRedeemablePoints > 0) {
          if (maxRedeemablePoints > pointsRemaining) {
            pointsValue += pointsRemaining / product.loyaltyPointRedeemValue;
            pointsRemaining = 0;
          } else {
            pointsValue +=
              maxRedeemablePoints / product.loyaltyPointRedeemValue;
            pointsRemaining = pointsRemaining - maxRedeemablePoints;
          }
        }
        i--;
      }
      return round2Decimal(pointsValue);
    }) as any
  );

  maxDiscountGivenLoyaltyPoints_ASYNC = async (points: number) => {
    const allItems = await this.items.fetch();
    const discountableItems = [];
    let pointsRemaining = points;
    let pointsValue = 0;

    for (const item of allItems) {
      if (await item.discountableItem_ASYNC()) {
        discountableItems.push(item);
      }
    }
    let i = allItems.length;
    while (pointsRemaining >= 1 && i >= 1) {
      const item = discountableItems[i];
      const product = await item.product.fetch();
      const maxRedeemablePoints = await item.maxPossibleRedeemPoints_ASYNC();
      pointsValue = +maxRedeemablePoints * product.loyaltyPointRedeemValue;
      pointsRemaining = pointsRemaining - maxRedeemablePoints;
      i--;
    }

    return round2Decimal(pointsValue);
  };

  get isSale(): boolean {
    return (
      this.saleState == "OPEN" ||
      this.saleState == "SAVED" ||
      this.saleState == "CLOSED"
    );
  }

  get isProforma(): boolean {
    return this.saleState.includes("PROFORMA");
  }

  get isLayby(): boolean {
    return this.saleState.includes("LAYBY");
  }

  get isInvoice(): boolean {
    return this.saleState.includes("INVOICE");
  }

  get settledPayments() {
    return this.payments.filter((p) => p.state === "Settled" || !p.state);
  }

  get isClosed() {
    return this.saleState.includes("CLOSED");
  }

  get isQuote() {
    return this.saleState.includes("QUOTE");
  }

  get saleType() {
    if (this.isLayby) {
      return "Layby";
    }
    if (this.isInvoice) {
      return "Invoice";
    }
    if (this.isQuote) {
      return "Quote";
    }
    if (this.isProforma) {
      return "Proforma";
    }
    return "Sale";
  }
}
